import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Grid, Paper, MenuItem, Select, FormControl,
  Table, TableBody, TableCell, TableHead, TableRow
} from "@mui/material";
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer
} from "recharts";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { COLLECTION } from "../../../../data/constant";

const RANGE_OPTIONS = [7, 15, 30, 90, 180];
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AA336A", "#663399", "#FF5555", "#33CC33"];

const ProductsTab = () => {
  const [walletDeducts, setWalletDeducts] = useState([]);
  const [topRange, setTopRange] = useState(30);
  const [leastRange, setLeastRange] = useState(30);
  const [productStats, setProductStats] = useState({});

  // Fetch WalletDeduction data
  useEffect(() => {
    const fetchData = async () => {
      const snap = await getDocs(collection(db, COLLECTION.WALLET_DEDUCTIONS));
      setWalletDeducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchData();
  }, []);

  // Filter wallet data by date range for top products
  const filteredTopData = useMemo(() => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - topRange + 1);

    return walletDeducts.filter(tx => Array.isArray(tx.Itemspicked) && tx.Timestamp && tx.Timestamp.toDate() >= startDate && tx.Timestamp.toDate() <= today);
  }, [walletDeducts, topRange]);

  // Filter wallet data by date range for least products
  const filteredLeastData = useMemo(() => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - leastRange + 1);

    return walletDeducts.filter(tx => Array.isArray(tx.Itemspicked) && tx.Timestamp && tx.Timestamp.toDate() >= startDate && tx.Timestamp.toDate() <= today);
  }, [walletDeducts, leastRange]);

  // Compute product stats function
  const computeStats = (data) => {
    const stats = {};
    data.forEach(tx => {
      tx.Itemspicked.forEach(item => {
        const name = item.name || "Unknown";
        const quantity = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        const revenue = price * quantity;

        if (!stats[name]) stats[name] = { quantity: 0, revenue: 0 };
        stats[name].quantity += quantity;
        stats[name].revenue += revenue;
      });
    });
    return stats;
  };

  const topProductStats = useMemo(() => computeStats(filteredTopData), [filteredTopData]);
  const leastProductStats = useMemo(() => computeStats(filteredLeastData), [filteredLeastData]);

  // Top 10 products by revenue
  const topProducts = useMemo(() => {
    return Object.entries(topProductStats)
      .sort((a, b) => b[1].revenue - a[1].revenue)
      .slice(0, 10)
      .map(([name, data]) => ({ name, ...data }));
  }, [topProductStats]);

  // Bottom 10 products by quantity
  const leastProducts = useMemo(() => {
    return Object.entries(leastProductStats)
      .sort((a, b) => a[1].quantity - b[1].quantity)
      .slice(0, 10)
      .map(([name, data]) => ({ name, ...data }));
  }, [leastProductStats]);

  return (
    <Box sx={{ mt: 2, fontFamily: "'Montserrat', sans-serif" }}>
      {/* ---------------- TOP CHARTS ---------------- */}
      <Grid container spacing={3} justifyContent="space-between">
        {/* Top Products Revenue */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 250 }}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 280 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>Top Products Revenue</Typography>
              <FormControl size="small">
                <Select value={topRange} onChange={(e) => setTopRange(Number(e.target.value))}>
                  {RANGE_OPTIONS.map(v => <MenuItem key={v} value={v}>{v}d</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={topProducts}
                  dataKey="revenue"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={(entry) => entry.name}
                >
                  {topProducts.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Least Products Sold */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 250 }}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 280 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>Least Products Sold</Typography>
              <FormControl size="small">
                <Select value={leastRange} onChange={(e) => setLeastRange(Number(e.target.value))}>
                  {RANGE_OPTIONS.map(v => <MenuItem key={v} value={v}>{v}d</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={leastProducts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="quantity">
                  {leastProducts.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* ---------------- DETAILED TABLES ---------------- */}
      <Grid container spacing={3} sx={{ mt: 3 }} justifyContent="space-between">
        {/* Top Products Table */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 300 }}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontFamily: "Montserrat" }}>Top Products Details</Typography>
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: "Montserrat" }}>Product</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "Montserrat" }}>Quantity</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "Montserrat" }}>Revenue (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topProducts.map((p, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontFamily: "Montserrat" }}>{p.name}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: "Montserrat" }}>{p.quantity}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: "Montserrat" }}>{p.revenue.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Least Products Table */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 300 }}>
          <Typography variant="subtitle2" color="textSecondary"sx={{ mb: 1, fontFamily: "Montserrat" }}>Least Products Details</Typography>
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>Product</TableCell>
                  <TableCell align="right" variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>Quantity</TableCell>
                  <TableCell align="right" svariant="subtitle2" color="textSecondary" x={{ fontFamily: "Montserrat" }}>Revenue (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leastProducts.map((p, idx) => (
                  <TableRow key={idx}>
                    <TableCell variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>{p.name}</TableCell>
                    <TableCell align="right" variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>{p.quantity}</TableCell>
                    <TableCell align="right" variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>{p.revenue.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductsTab;
